/**
 * themeColors.ts — Runtime CSS-variable reader for chart libraries.
 *
 * Why this exists:
 *   Tailwind utilities resolve CSS vars at build time via the
 *   `rgb(var(--x) / <alpha-value>)` pattern. Chart libraries (Recharts,
 *   react-circular-progressbar) take colors as plain string props
 *   (`stroke`, `fill`, `pathColor`) and cannot consume CSS vars directly.
 *   This module bridges the two: at render time it reads the active theme's
 *   CSS variable values via getComputedStyle() and returns the concrete
 *   color strings the chart libs accept.
 *
 * Critical timing rule:
 *   Call `getThemeColors()` (and `getScoreColors()`) INSIDE component render
 *   functions, never at module top level. `getComputedStyle()` reads the
 *   CURRENT theme's CSS variable values. Module top-level execution may run
 *   before `<html>.light` is set, returning stale dark-mode values that
 *   won't update on theme toggle.
 *
 *   To trigger re-renders on theme toggle, parent chart components should
 *   subscribe to `useTheme()` so children re-render when the value changes.
 */

import type { Category } from '@/components/carbon/types'

/**
 * Read a CSS variable's computed value from :root: a bare RGB-channel string
 * for the legacy `--color-*` channels (e.g. "118 214 157"), a full color for
 * the Carbon tokens. Returns "" if the var is missing or we're running
 * outside a browser (SSR).
 */
export function cssVar(name: string): string {
  if (typeof document === 'undefined') return ''
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim()
}

/**
 * Convert a `--color-x` channel var into a chart-library-friendly color string.
 *
 * @param varName  CSS var name including leading `--`, e.g. "--color-primary-500".
 * @param alpha    Optional alpha 0-1; produces `rgba(R, G, B, A)` when supplied.
 * @returns        `rgb(R, G, B)`, `rgba(R, G, B, A)`, or `"transparent"` when the
 *                 variable is missing/malformed (safe default — won't crash SVG).
 */
export function toRgb(varName: string, alpha?: number): string {
  const channels = cssVar(varName)
  // Whitespace-tolerant split — bare-channel format is "R G B" but editors
  // sometimes leave extra spaces or tabs around the value.
  const [r, g, b] = channels.split(/\s+/).filter(Boolean)
  if (!r || !g || !b) return 'transparent'

  return alpha === undefined
    ? `rgb(${r}, ${g}, ${b})`
    : `rgba(${r}, ${g}, ${b}, ${alpha})`
}

export interface ThemeColors {
  // Surfaces (theme-driven)
  surface: string             // dark-800: cards, elevated surfaces
  surfaceAlt: string          // dark-700: hover, tertiary
  surfaceMuted: string        // dark-900: page bg, deep wells
  border: string              // dark-600: borders, axis lines
  muted: string               // dark-500: muted UI

  // Chart infrastructure (theme-driven)
  chartGrid: string           // CartesianGrid stroke
  chartAxis: string           // axis line / tick line stroke
  chartAxisLabel: string      // axis tick label fill
  chartActiveDotFill: string  // activeDot fill — sits on top of the line, blends into surface
  chartTooltipBg: string      // custom tooltip background = surface
  chartTooltipBorder: string  // custom tooltip border = border

  // Brand (theme-driven)
  primary: string             // primary-500: Soft Green (dark) / deeper green (light)
  primaryGlow: string         // always transparent: Carbon has no glow
  secondaryBlue: string       // secondary-blue-300: Horizon Blue
  secondarySand: string       // secondary-sand-300: Warm Sand

  // Severity — read from the --sev-* tokens, so they follow the theme. Only
  // critical takes a hue (Coral); high, medium, low and info are neutrals.
  // The *Glow fields are kept for the API and are always transparent:
  // Carbon has no glow (Layer-Not-Shadow Rule).
  critical: string
  criticalGlow: string
  high: string
  highGlow: string
  medium: string
  mediumGlow: string
  low: string
  lowGlow: string
  info: string
  infoGlow: string

  // Score (CircularProgressbar buildStyles). Neutral Bad State Rule: a score
  // band never takes a hue; a low score only drops to secondary ink.
  trailColor: string          // dark-700: ring trail behind score arc
  scoreGood: string           // text-primary: >= 80
  scoreGoodGlow: string
  scoreGoodText: string
  scoreFair: string           // text-primary: 60-79
  scoreFairGlow: string
  scoreFairText: string
  scorePoor: string           // text-secondary: < 60
  scorePoorGlow: string
  scorePoorText: string
}

/** No glow anywhere in Carbon; the *Glow fields resolve to this. */
const NO_GLOW = 'transparent'

/**
 * Read a full-value Carbon token (hex, rgba, or a resolved var() alias) for a
 * chart prop. `transparent` when the token is missing or outside a browser,
 * the same safe default `toRgb` uses.
 */
function tokenColor(name: string): string {
  return cssVar(name) || 'transparent'
}

/**
 * Snapshot the active theme's chart-relevant colors.
 *
 * IMPORTANT: call inside a component render function, not at module scope.
 * Each invocation reads CSS vars fresh, so theme toggles take effect on the
 * next render of any component that calls this.
 */
export function getThemeColors(): ThemeColors {
  const surface       = toRgb('--color-dark-800')
  const surfaceAlt    = toRgb('--color-dark-700')
  const border        = toRgb('--color-dark-600')
  const primary       = toRgb('--color-primary-500')
  const secondarySand = toRgb('--color-secondary-sand-300')
  const textPrimary   = tokenColor('--text-primary')

  return {
    // Surfaces
    surface,
    surfaceAlt,
    surfaceMuted:    toRgb('--color-dark-900'),
    border,
    muted:           toRgb('--color-dark-500'),

    // Chart infrastructure (--color-chart-* added in Phase 1)
    chartGrid:          toRgb('--color-chart-grid'),
    chartAxis:          border,
    chartAxisLabel:     toRgb('--color-chart-axis-label'),
    chartActiveDotFill: toRgb('--color-chart-active-dot'),
    chartTooltipBg:     surface,
    chartTooltipBorder: border,

    // Brand
    primary,
    primaryGlow:    NO_GLOW,
    secondaryBlue:  toRgb('--color-secondary-blue-300'),
    secondarySand,

    // Severity — theme-aware tokens; Coral for critical only.
    critical:     tokenColor('--sev-crit'),
    criticalGlow: NO_GLOW,
    high:         tokenColor('--sev-high'),
    highGlow:     NO_GLOW,
    medium:       tokenColor('--sev-med'),
    mediumGlow:   NO_GLOW,
    low:          tokenColor('--sev-low'),
    lowGlow:      NO_GLOW,
    info:         tokenColor('--info'),
    infoGlow:     NO_GLOW,

    // Score gauge: neutral ink in every band (Neutral Bad State Rule).
    trailColor:      surfaceAlt,
    scoreGood:       textPrimary,
    scoreGoodGlow:   NO_GLOW,
    scoreGoodText:   textPrimary,
    scoreFair:       textPrimary,
    scoreFairGlow:   NO_GLOW,
    scoreFairText:   textPrimary,
    scorePoor:       tokenColor('--text-secondary'),
    scorePoorGlow:   NO_GLOW,
    scorePoorText:   textPrimary,
  }
}

/** The four series a Guardian chart can draw; Secure Score is Microsoft's. */
export type ChartCategory = Category

export interface ChartTheme {
  grid: string                // CartesianGrid stroke: --border-subtle
  axis: string                // axis line and tick labels: --text-helper
  tooltipBg: string           // --layer-02
  tooltipBorder: string       // --border-subtle
  tooltipText: string         // --text-primary
  activeDotFill: string       // --layer-01, so a hovered point reads as a cutout
  neutral: string             // an uncategorized series (Sparkline default): --text-primary
  series: Record<ChartCategory, string>     // line and mark color: --cat-*
  seriesInk: Record<ChartCategory, string>  // end-label text: --cat-*-ink
  /** strokeDasharray per series; only Secure Score is dashed (Microsoft's, not a pillar). */
  dash: Record<ChartCategory, string | undefined>
  strokeWidth: number         // 1.75px lines
  markerSize: number          // 7px square end markers
}

/**
 * Snapshot the Carbon chart theme (DESIGN.md "Trend chart"). Same timing rule
 * as `getThemeColors()`: call inside render, never at module scope.
 */
export function getChartTheme(): ChartTheme {
  const borderSubtle = tokenColor('--border-subtle')
  return {
    grid: borderSubtle,
    axis: tokenColor('--text-helper'),
    tooltipBg: tokenColor('--layer-02'),
    tooltipBorder: borderSubtle,
    tooltipText: tokenColor('--text-primary'),
    activeDotFill: tokenColor('--layer-01'),
    neutral: tokenColor('--text-primary'),
    series: {
      baseline: tokenColor('--cat-baseline'),
      bpa: tokenColor('--cat-bpa'),
      identity: tokenColor('--cat-identity'),
      secure: tokenColor('--cat-secure'),
    },
    seriesInk: {
      baseline: tokenColor('--cat-baseline-ink'),
      bpa: tokenColor('--cat-bpa-ink'),
      identity: tokenColor('--cat-identity-ink'),
      secure: tokenColor('--cat-secure-ink'),
    },
    dash: { baseline: undefined, bpa: undefined, identity: undefined, secure: '4 3' },
    strokeWidth: 1.75,
    markerSize: 7,
  }
}

export interface ScoreColorPair {
  color: string
  glow: string
  text: string
  trail: string
}

/**
 * Bracket a numeric score (0-100) into the matching theme color set.
 * Mirrors the shape used by `complianceColors.ts` so call sites can swap in
 * cleanly. Brackets: >= 80 → good, >= 60 → fair, else → poor.
 */
export function getScoreColors(score: number): ScoreColorPair {
  const tc = getThemeColors()
  const tier =
    score >= 80 ? { color: tc.scoreGood, glow: tc.scoreGoodGlow, text: tc.scoreGoodText } :
    score >= 60 ? { color: tc.scoreFair, glow: tc.scoreFairGlow, text: tc.scoreFairText } :
                  { color: tc.scorePoor, glow: tc.scorePoorGlow, text: tc.scorePoorText }
  return { ...tier, trail: tc.trailColor }
}
